import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  Users,
  RefreshCw,
  Trash2,
  Plus,
  Loader2,
  Search,
  Columns3,
  ArrowUpDown,
  MapPin,
  Link2,
  Phone,
  FileText,
  Settings2,
  ChevronUp,
  ChevronDown,
  CheckCircle2,
  AlertTriangle,
  Coins,
  Wallet,
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import OriginPickerMap from "../../components/admin/OriginPickerMap";
import WhatsAppFamigliaDialog from "../../components/admin/whatsapp/WhatsAppFamigliaDialog";
import WhatsAppSchedaLeadDialog from "../../components/admin/whatsapp/WhatsAppSchedaLeadDialog";
import WhatsAppRichiestaPostiDialog from "../../components/admin/whatsapp/WhatsAppRichiestaPostiDialog";
import WhatsAppDisponibilitaBadge from "../../components/admin/whatsapp/WhatsAppDisponibilitaBadge";
import { AdminPageHeader } from "./components/AdminUi";
import PratichePipelineBoard from "./cdrpt/PratichePipelineBoard";
import PraticaDetailPage from "./cdrpt/PraticaDetailPage";

const API = process.env.REACT_APP_BACKEND_URL;
const COLS_KEY = "affidya_leads_grid_cols_v4";

const LEAD_STATUS_OPTIONS = [
  "Nuovo",
  "Contattato",
  "Non risponde",
  "Perso",
  "Richiamare",
  "Contattare 24h",
  "Contattare 1 settimana",
  "Contattare 14gg",
  "Contattare 1 mese",
];

const VIEW_TABS = [
  { id: "tutti", label: "Tutti" },
  ...LEAD_STATUS_OPTIONS.map((status) => ({ id: status, label: status })),
  { id: "ospiti_rete", label: "Ospiti in Rete" },
];

const LEAD_STATUS_STYLES = {
  NUOVO: "bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100",
  "NON CONTATTATO": "bg-lime-100 text-lime-900 border-lime-200 hover:bg-lime-100",
  CONTATTATO: "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100",
  "NON RISPONDE": "bg-amber-100 text-amber-900 border-amber-200 hover:bg-amber-100",
  RICHIAMARE: "bg-amber-100 text-amber-900 border-amber-200 hover:bg-amber-100",
  "RICHIAMARE 24H": "bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-100",
  "CONTATTARE 24H": "bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-100",
  "CONTATTARE 1 SETTIMANA": "bg-yellow-100 text-yellow-900 border-yellow-200 hover:bg-yellow-100",
  "RICHIAMARE 1-2 SETT.": "bg-violet-100 text-violet-800 border-violet-200 hover:bg-violet-100",
  "CONTATTARE 14GG": "bg-violet-100 text-violet-800 border-violet-200 hover:bg-violet-100",
  "CONTATTARE TRA 1-2 MESI": "bg-sky-100 text-sky-800 border-sky-200 hover:bg-sky-100",
  "CONTATTARE 1 MESE": "bg-sky-100 text-sky-800 border-sky-200 hover:bg-sky-100",
  "CONTATTARE IN FUTURO": "bg-indigo-100 text-indigo-800 border-indigo-200 hover:bg-indigo-100",
  "TRATTATIVA IN CORSO": "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200 hover:bg-fuchsia-100",
  "GIA' IN TRATTATIVA": "bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-100",
  "TRATTATIVA CONCLUSA": "bg-teal-100 text-teal-800 border-teal-200 hover:bg-teal-100",
  "BUDGET BASSO": "bg-yellow-100 text-yellow-900 border-yellow-200 hover:bg-yellow-100",
  PERSO: "bg-rose-100 text-rose-800 border-rose-200 hover:bg-rose-100",
  "DECEDUTO/A": "bg-stone-200 text-stone-800 border-stone-300 hover:bg-stone-200",
};

function leadStatusClass(status) {
  const key = String(status || "").trim().toUpperCase();
  return LEAD_STATUS_STYLES[key] || "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-100";
}

function LeadStatusBadge({ status }) {
  const label = String(status || "").trim() || "—";
  return (
    <Badge variant="outline" className={leadStatusClass(label === "—" ? "" : label)}>
      {label}
    </Badge>
  );
}

const ALL_COLUMNS = [
  { id: "data_inserimento", label: "Data inserimento", default: true },
  { id: "nome_lead", label: "Nome lead", default: true },
  { id: "telefono", label: "Telefono", default: true },
  { id: "struttura_affidya", label: "Struttura che condivide", default: false },
  { id: "city", label: "Comune di residenza", default: true },
  { id: "zone_accettate", label: "Zone accettate", default: true },
  { id: "budget", label: "Budget", default: true },
  { id: "lead_status", label: "Stato lead", default: true },
  { id: "fase_pratica", label: "Fase pratica CDRPT", default: false },
  { id: "responsabile_pratica", label: "Responsabile pratica", default: false },
  { id: "prossimo_ricontatto", label: "Prossimo ricontatto", default: false },
  { id: "note_contatto", label: "Note ultimo contatto", default: true },
  { id: "origine_label", label: "Origine", default: true },
  { id: "struttura_proposta_1", label: "Struttura proposta 1", default: true },
  { id: "stato_interazione_1", label: "Stato proposta 1", default: true },
  { id: "struttura_proposta_2", label: "Struttura proposta 2", default: true },
  { id: "stato_interazione_2", label: "Stato proposta 2", default: true },
  { id: "data_ultimo_contatto", label: "Data ultimo contatto", default: true },
  { id: "email", label: "Email", default: false },
  { id: "gettone_importo_totale", label: "Importo gettone", default: false },
  { id: "gettone_stato", label: "Stato gettone", default: false },
  { id: "rete_stato", label: "Stato pratica rete", default: false },
];

const EMPTY = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  mobile: "",
  titolo_ospite: "",
  nome_ospite: "",
  cognome_ospite: "",
  sesso: "",
  eta: "",
  city: "",
  state: "",
  zip_code: "",
  street: "",
  budget: "",
  note_contatto: "",
  description: "",
  lead_source: "",
  lead_status: "Nuovo",
  relazione_referente: "",
  portale_origine: "",
  canale_acquisizione: "",
  zona_richiesta: "",
  zone_accettate: "",
  autosufficienza: "",
  urgenza: "",
};

const ECONOMICO_EMPTY = {
  economico_ingresso_effettivo_at: "",
  economico_fattura_1_inviata_at: "",
  economico_fattura_2_inviata_at: "",
  economico_pagamento_ricevuto_at: "",
  economico_pagamento_importo: "",
  economico_note: "",
  economico_annullato: false,
};

function economicoFormFromLead(lead) {
  if (!lead) return { ...ECONOMICO_EMPTY };
  const dateVal = (v) => (v ? String(v).slice(0, 10) : "");
  return {
    economico_ingresso_effettivo_at: dateVal(lead.economico_ingresso_effettivo_at),
    economico_fattura_1_inviata_at: dateVal(lead.economico_fattura_1_inviata_at),
    economico_fattura_2_inviata_at: dateVal(lead.economico_fattura_2_inviata_at),
    economico_pagamento_ricevuto_at: dateVal(lead.economico_pagamento_ricevuto_at),
    economico_pagamento_importo: lead.economico_pagamento_importo ?? "",
    economico_note: lead.economico_note || "",
    economico_annullato: !!lead.economico_annullato,
  };
}

function defaultOrigineFromLead(lead) {
  if (!lead) return "";
  const street = (lead.street || "").trim();
  const city = (lead.city || "").trim();
  const zona = (lead.zona_richiesta || "").trim();
  const zoneAcc = (lead.zone_accettate || "").trim();
  if (street) return [street, city].filter(Boolean).join(" ");
  if (zona) return city && !zona.toLowerCase().includes(city.toLowerCase()) ? `${zona} ${city}` : zona;
  if (zoneAcc) {
    const first = zoneAcc.split(",")[0].trim();
    if (first) {
      return city && !first.toLowerCase().includes(city.toLowerCase()) ? `${first} ${city}` : first;
    }
  }
  return city;
}

function budgetInputFromLead(val) {
  if (val == null || val === "") return "";
  const raw = String(val).replace(/€/g, "").trim();
  const normalized = /,\d{2}$/.test(raw.replace(/\s/g, ""))
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw.replace(",", ".");
  const num = Number(String(normalized).replace(/[^\d.-]/g, ""));
  return Number.isFinite(num) && num > 0 ? String(Math.round(num)) : "";
}

function loadCols() {
  try {
    const raw = localStorage.getItem(COLS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) return parsed;
    }
  } catch { /* ignore */ }
  return ALL_COLUMNS.filter((c) => c.default).map((c) => c.id);
}

function contattoLabel(contatto) {
  const ruolo = String(contatto?.ruolo || "").trim();
  const nome = String(contatto?.nome || "").trim();
  if (nome && ruolo) return `${nome} (${ruolo.toLowerCase()})`;
  return nome || ruolo || "Telefono";
}

function ContattiStruttura({ contatti }) {
  const items = (Array.isArray(contatti) ? contatti : []).filter((c) => c?.telefono);
  if (!items.length) return null;
  return (
    <div className="flex flex-col gap-1 border-l border-slate-200 pl-3 text-left">
      {items.map((c) => {
        const tel = String(c.telefono);
        const label = contattoLabel(c);
        return (
          <a
            key={`${c.ruolo || ""}-${tel}`}
            href={`tel:${tel.replace(/[^\d+]/g, "")}`}
            className="flex items-baseline gap-1.5 whitespace-nowrap text-xs text-slate-700 hover:text-blue-700"
            title={`Chiama ${label}`}
          >
            <Phone className="h-3 w-3 shrink-0 self-center text-slate-400" />
            <span className="text-slate-500">{label}</span>
            <span className="font-medium tabular-nums text-slate-900">{tel}</span>
          </a>
        );
      })}
    </div>
  );
}

function cellValue(lead, colId) {
  switch (colId) {
    case "nome_lead":
      return (
        lead.nome_lead ||
        `${lead.first_name || ""} ${lead.last_name || ""}`.trim() ||
        `${lead.nome_ospite || ""} ${lead.cognome_ospite || ""}`.trim() ||
        "—"
      );
    case "telefono":
      return lead.phone || lead.mobile || "—";
    case "gettone_importo_totale":
      return lead.gettone_importo_totale != null ? `${lead.gettone_importo_totale} €` : "—";
    case "gettone_stato":
      return lead.gettone_stato || lead.rete_pratica?.stato_gettone || "—";
    case "rete_stato":
      return lead.rete_pratica?.stato || "—";
    case "fase_pratica":
      return lead.pratica?.fase ? lead.pratica.fase.replaceAll("_", " ") : "—";
    case "responsabile_pratica":
      return lead.pratica?.responsabile?.nome || "Non assegnata";
    case "prossimo_ricontatto":
      return lead.prossima_attivita?.scadenza_at
        ? new Date(lead.prossima_attivita.scadenza_at).toLocaleString("it-IT")
        : "—";
    case "struttura_affidya":
      return lead.struttura_affidya || lead.rete_pratica?.struttura_nome || "—";
    case "data_inserimento": {
      const v = lead.data_condivisione_rete || lead.data_inserimento;
      if (!v) return "—";
      try {
        return new Date(v).toLocaleDateString("it-IT");
      } catch {
        return String(v).slice(0, 10);
      }
    }
    case "data_ultimo_contatto": {
      const v = lead.data_ultimo_contatto;
      if (!v) return "—";
      try {
        return new Date(v).toLocaleDateString("it-IT");
      } catch {
        return String(v).slice(0, 10);
      }
    }
    default:
      return lead[colId] ?? "—";
  }
}

export default function LeadsAdminModule({
  canManageGettoni = false,
  canManagePratiche = false,
}) {
  const token = localStorage.getItem("super_admin_token");
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const [items, setItems] = useState([]);
  const [workspaceView, setWorkspaceView] = useState(() => new URLSearchParams(window.location.search).get("cdrptView") || "elenco");
  const [pipelineDetailId, setPipelineDetailId] = useState(() => new URLSearchParams(window.location.search).get("pratica") || "");
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [viewTab, setViewTab] = useState("Nuovo");
  const [sourceFilter, setSourceFilter] = useState("");
  const [sort, setSort] = useState("data_inserimento");
  const [order, setOrder] = useState("desc");
  const [visibleCols, setVisibleCols] = useState(loadCols);
  const [colsOpen, setColsOpen] = useState(false);
  const isReteTab = viewTab === "ospiti_rete";

  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [nota, setNota] = useState("");
  const [gettoneForm, setGettoneForm] = useState({});
  const [economicoForm, setEconomicoForm] = useState({ ...ECONOMICO_EMPTY });
  const [economicoSaving, setEconomicoSaving] = useState(false);
  const [gettoneSaving, setGettoneSaving] = useState(false);
  const [praticaForm, setPraticaForm] = useState({});
  const [praticaSaving, setPraticaSaving] = useState(false);

  const [matchOpen, setMatchOpen] = useState(false);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchResults, setMatchResults] = useState([]);
  const [matchMeta, setMatchMeta] = useState(null);
  const [raggio, setRaggio] = useState(30);
  const [origine, setOrigine] = useState("");
  const [budgetMax, setBudgetMax] = useState("");
  const [includiOltreBudget, setIncludiOltreBudget] = useState(false);
  const [originSuggestions, setOriginSuggestions] = useState([]);
  const [originSuggestOpen, setOriginSuggestOpen] = useState(false);
  const [originSuggestLoading, setOriginSuggestLoading] = useState(false);
  const [originSelected, setOriginSelected] = useState(null);

  const [associaOpen, setAssociaOpen] = useState(false);
  const [associaTarget, setAssociaTarget] = useState(null);
  const [sendBothSchede, setSendBothSchede] = useState(false);
  const [associaForm, setAssociaForm] = useState({
    azione_invio: "solo_registra",
    email_testo: "",
    nascondi_nome: false,
    nascondi_email: true,
    destinatario_lead: "",
    destinatario_struttura: "",
  });
  const [associating, setAssociating] = useState(false);
  const [familyWhatsAppOpen, setFamilyWhatsAppOpen] = useState(false);
  const [structureWhatsAppTarget, setStructureWhatsAppTarget] = useState(null);
  const [structureWhatsAppMode, setStructureWhatsAppMode] = useState("");

  const [inboundOpen, setInboundOpen] = useState(false);
  const [inboundCfg, setInboundCfg] = useState({ email_inbox: "", enabled: false });
  const [crmUsers, setCrmUsers] = useState([]);
  const [crmForm, setCrmForm] = useState({ email: "", password: "", name: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort, order, limit: "500" });
      if (q.trim()) params.set("q", q.trim());
      if (viewTab === "ospiti_rete") {
        params.set("solo_rete", "true");
      } else if (viewTab !== "tutti") {
        params.set("lead_status", viewTab);
        params.set("escludi_rete", "true");
      }
      if (sourceFilter && viewTab !== "ospiti_rete") params.set("origine", sourceFilter);
      const res = await fetch(`${API}/api/admin/leads?${params}`, { headers });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Errore caricamento lead");
      }
      const json = await res.json();
      setItems(json.data || []);
    } catch (e) {
      toast.error(e.message || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, [q, viewTab, sourceFilter, sort, order]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const persistCols = (cols) => {
    setVisibleCols(cols);
    localStorage.setItem(COLS_KEY, JSON.stringify(cols));
  };

  const toggleSort = (colId) => {
    if (sort === colId) setOrder((o) => (o === "desc" ? "asc" : "desc"));
    else {
      setSort(colId);
      setOrder("asc");
    }
  };

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY);
    setEditOpen(true);
  };

  const loadRetePratica = async (richiestaId) => {
    if (!richiestaId || !canManagePratiche) return;
    try {
      const res = await fetch(`${API}/api/admin/ospiti-rete/condivisi/${richiestaId}`, { headers });
      if (!res.ok) throw new Error("Impossibile caricare la pratica rete");
      const pratica = await res.json();
      setDetail((prev) => prev ? { ...prev, rete_pratica: pratica } : prev);
      setPraticaForm({
        stato: pratica.stato || "Condiviso in rete",
        stato_gettone: pratica.stato_gettone || "In attesa",
        gettone_previsto: pratica.gettone_previsto ?? "",
        data_maturazione: pratica.data_maturazione ? String(pratica.data_maturazione).slice(0, 10) : "",
        data_saldo: pratica.data_saldo ? String(pratica.data_saldo).slice(0, 10) : "",
        note_rete: pratica.note_rete || "",
      });
    } catch (e) {
      toast.error(e.message || "Errore caricamento pratica rete");
    }
  };

  const openDetail = async (lead) => {
    try {
      const res = await fetch(`${API}/api/admin/leads/${lead.id}`, { headers });
      const json = await res.json();
      const fullLead = json.lead || lead;
      setDetail(fullLead);
      setGettoneForm(fullLead.gettone_data || {});
      setEconomicoForm(economicoFormFromLead(fullLead));
      setPraticaForm({});
      setDetailOpen(true);
      if (fullLead.ospiti_rete_richiesta_id) {
        await loadRetePratica(fullLead.ospiti_rete_richiesta_id);
      }
    } catch {
      setDetail(lead);
      setGettoneForm(lead.gettone_data || {});
      setEconomicoForm(economicoFormFromLead(lead));
      setDetailOpen(true);
    }
  };

  const saveLead = async () => {
    setSaving(true);
    try {
      const url = editId ? `${API}/api/admin/leads/${editId}` : `${API}/api/admin/leads`;
      const res = await fetch(url, {
        method: editId ? "PUT" : "POST",
        headers,
        body: JSON.stringify(form),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Salvataggio fallito");
      const saved = json.lead;
      if (saved?.id) {
        setItems((rows) => rows.map((row) => (row.id === saved.id ? { ...row, ...saved } : row)));
        if (detail?.id === saved.id) {
          setDetail((prev) => ({ ...(prev || {}), ...saved }));
        }
      }
      toast.success(editId ? "Lead aggiornato" : "Lead creato");
      setEditOpen(false);
      if (editId && detail?.id === editId) {
        try {
          const freshRes = await fetch(`${API}/api/admin/leads/${editId}`, { headers });
          const fresh = await freshRes.json().catch(() => ({}));
          if (freshRes.ok && fresh.lead) {
            setDetail(fresh.lead);
            setGettoneForm(fresh.lead.gettone_data || {});
            setEconomicoForm(economicoFormFromLead(fresh.lead));
            if (fresh.lead.ospiti_rete_richiesta_id) {
              await loadRetePratica(fresh.lead.ospiti_rete_richiesta_id);
            }
          }
        } catch {
          /* il merge locale resta nel pannello */
        }
      }
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Eliminare questo lead?")) return;
    try {
      const res = await fetch(`${API}/api/admin/leads/${id}`, { method: "DELETE", headers });
      if (!res.ok) throw new Error("Eliminazione fallita");
      toast.success("Eliminato");
      setDetailOpen(false);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const addNota = async () => {
    if (!detail?.id || !nota.trim()) return;
    try {
      const res = await fetch(`${API}/api/admin/leads/${detail.id}/note`, {
        method: "POST",
        headers,
        body: JSON.stringify({ testo: nota.trim() }),
      });
      if (!res.ok) throw new Error("Errore salvataggio nota");
      toast.success("Nota aggiunta");
      setNota("");
      openDetail(detail);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };


  const saveEconomico = async () => {
    if (!detail?.id) return;
    setEconomicoSaving(true);
    try {
      const payload = {
        economico_ingresso_effettivo_at: economicoForm.economico_ingresso_effettivo_at || "",
        economico_fattura_1_inviata_at: economicoForm.economico_fattura_1_inviata_at || "",
        economico_fattura_2_inviata_at: economicoForm.economico_fattura_2_inviata_at || "",
        economico_pagamento_ricevuto_at: economicoForm.economico_pagamento_ricevuto_at || "",
        economico_pagamento_importo: economicoForm.economico_pagamento_importo === "" || economicoForm.economico_pagamento_importo == null
          ? null
          : Number(economicoForm.economico_pagamento_importo),
        economico_note: economicoForm.economico_note || "",
        economico_annullato: !!economicoForm.economico_annullato,
      };
      const res = await fetch(`${API}/api/admin/leads/${detail.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore salvataggio economico");
      toast.success("Dati economici CDRPT aggiornati");
      await openDetail(detail);
      load();
    } catch (e) {
      toast.error(e.message || "Errore salvataggio economico");
    } finally {
      setEconomicoSaving(false);
    }
  };

  const saveGettone = async () => {
    if (!detail?.ospiti_rete_id) return;
    setGettoneSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/ospiti-in-rete-crm/${detail.ospiti_rete_id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(gettoneForm),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore salvataggio gettone");
      toast.success("Gettone aggiornato");
      await openDetail(detail);
      load();
    } catch (e) {
      toast.error(e.message || "Errore salvataggio gettone");
    } finally {
      setGettoneSaving(false);
    }
  };

  const saveRetePratica = async () => {
    if (!detail?.ospiti_rete_richiesta_id) return;
    setPraticaSaving(true);
    try {
      const payload = {
        ...praticaForm,
        gettone_previsto:
          praticaForm.gettone_previsto === "" ? null : Number(praticaForm.gettone_previsto),
      };
      const res = await fetch(
        `${API}/api/admin/ospiti-rete/condivisi/${detail.ospiti_rete_richiesta_id}`,
        { method: "PATCH", headers, body: JSON.stringify(payload) }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore salvataggio pratica rete");
      toast.success("Pratica rete aggiornata");
      await loadRetePratica(detail.ospiti_rete_richiesta_id);
      load();
    } catch (e) {
      toast.error(e.message || "Errore salvataggio pratica rete");
    } finally {
      setPraticaSaving(false);
    }
  };

  useEffect(() => {
    if (!matchOpen) return undefined;
    const qOrigin = origine.trim();
    const selectedLabel = (originSelected?.formatted_address || originSelected?.label || "").trim();
    if (qOrigin.length < 2) {
      setOriginSuggestions([]);
      return undefined;
    }
    if (originSelected && qOrigin === selectedLabel) {
      return undefined;
    }
    const timer = setTimeout(async () => {
      setOriginSuggestLoading(true);
      try {
        const res = await fetch(
          `${API}/api/admin/leads/geocode-suggest?q=${encodeURIComponent(qOrigin)}`,
          { headers }
        );
        const json = await res.json();
        const list = json.suggestions || [];
        setOriginSuggestions(list);
        setOriginSuggestOpen(true);
      } catch {
        setOriginSuggestions([]);
      } finally {
        setOriginSuggestLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [origine, matchOpen, originSelected]);

  const pickOrigin = (place) => {
    setOrigine(place.formatted_address || place.label || "");
    setOriginSelected(place);
    setOriginSuggestOpen(false);
  };

  const handleOriginMapMove = async ({ lat, lng }) => {
    const fallback = `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`;
    let label = fallback;
    try {
      const res = await fetch(
        `${API}/api/admin/leads/geocode-reverse?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`,
        { headers }
      );
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.formatted_address) label = json.formatted_address;
    } catch {
      /* tieni le coordinate anche senza etichetta */
    }
    const place = {
      id: `mappa:${Number(lat).toFixed(5)}:${Number(lng).toFixed(5)}`,
      lat: Number(lat),
      lng: Number(lng),
      label,
      formatted_address: label,
      source: "mappa",
    };
    setOrigine(label);
    setOriginSelected(place);
    runMatching({ originSelected: place, origine: label });
  };

  const runMatching = async (overrides = {}) => {
    if (!detail?.id) return;
    const origineVal = overrides.origine ?? origine;
    const budgetVal = overrides.budgetMax ?? budgetMax;
    const raggioVal = overrides.raggio ?? raggio;
    const oltreBudget = overrides.includiOltreBudget ?? includiOltreBudget;
    const selected = overrides.originSelected !== undefined ? overrides.originSelected : originSelected;
    const parsedBudget = budgetVal === "" || budgetVal == null ? null : Number(budgetVal);
    setMatchLoading(true);
    setMatchOpen(true);
    const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
    const abortTimer = ctrl ? setTimeout(() => ctrl.abort(), 25000) : null;
    try {
      const res = await fetch(`${API}/api/admin/leads/${detail.id}/matching`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          raggio_km: Number(raggioVal) || 30,
          solo_attive: true,
          origine_indirizzo: (origineVal || "").trim() || null,
          origine_lat: selected?.lat ?? null,
          origine_lng: selected?.lng ?? null,
          origine_label: selected?.formatted_address || selected?.label || null,
          budget_max: Number.isFinite(parsedBudget) && parsedBudget > 0 ? parsedBudget : null,
          includi_oltre_budget: !!oltreBudget,
        }),
        signal: ctrl?.signal,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Matching fallito");
      setMatchResults(json.results || []);
      setMatchMeta(json);
    } catch (e) {
      const aborted = e?.name === "AbortError";
      toast.error(aborted ? "Matching troppo lento: riprova Ricalcola (le coordinate già in cache sono gratis)." : e.message);
      setMatchResults([]);
      setMatchMeta(null);
    } finally {
      if (abortTimer) clearTimeout(abortTimer);
      setMatchLoading(false);
    }
  };

  const openMatching = () => {
    if (!detail?.id) return;
    const nextOrigine = defaultOrigineFromLead(detail);
    const nextBudget = budgetInputFromLead(detail.budget);
    setOrigine(nextOrigine);
    setBudgetMax(nextBudget);
    setIncludiOltreBudget(false);
    setMatchResults([]);
    setMatchMeta(null);
    setOriginSelected(null);
    setOriginSuggestions([]);
    setMatchOpen(true);
  };

  const openAssocia = (row, { inviaSchede = false } = {}) => {
    setAssociaTarget(row);
    setSendBothSchede(inviaSchede);
    setAssociaForm({
      azione_invio: inviaSchede ? "entrambe" : "solo_registra",
      email_testo: `Gentile referente,\n\nin allegato le schede relative alla proposta con ${row.nome}.\n\nCordiali saluti,\nCasaDiRiposoPerTe`,
      nascondi_nome: false,
      nascondi_email: true,
      destinatario_lead: detail?.email || "",
      destinatario_struttura: row.email || "",
    });
    setAssociaOpen(true);
  };

  const confirmAssocia = async () => {
    if (!detail?.id || !associaTarget) return;
    setAssociating(true);
    try {
      const res = await fetch(`${API}/api/admin/leads/${detail.id}/associa`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          struttura_id: associaTarget.struttura_id,
          struttura_nome: associaTarget.nome,
          ...associaForm,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Associazione fallita");
      toast.success("Struttura associata");
      setAssociaOpen(false);
      setMatchOpen(false);
      if (json.lead) setDetail(json.lead);
      else openDetail(detail);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAssociating(false);
    }
  };

  const loadInbound = async () => {
    try {
      const res = await fetch(`${API}/api/admin/leads-inbound/config`, { headers });
      if (res.ok) setInboundCfg(await res.json());
      const ures = await fetch(`${API}/api/admin/crm/users`, { headers });
      if (ures.ok) {
        const uj = await ures.json();
        setCrmUsers(uj.data || []);
      }
      setInboundOpen(true);
    } catch {
      setInboundOpen(true);
    }
  };

  const createCrmUser = async () => {
    try {
      const res = await fetch(`${API}/api/admin/crm/users`, {
        method: "POST",
        headers,
        body: JSON.stringify({ ...crmForm, profile: "crm_operatore" }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore creazione utente");
      toast.success("Operatore CRM creato");
      setCrmForm({ email: "", password: "", name: "" });
      loadInbound();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const saveInbound = async () => {
    try {
      const res = await fetch(`${API}/api/admin/leads-inbound/config`, {
        method: "PUT",
        headers,
        body: JSON.stringify(inboundCfg),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore");
      toast.success("Configurazione salvata");
      setInboundOpen(false);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const previewLeadPdf = async () => {
    if (!detail?.id) return;
    try {
      const res = await fetch(
        `${API}/api/admin/leads/${detail.id}/scheda-pdf?nascondi_email=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        const detailMsg = json.detail;
        throw new Error(typeof detailMsg === "string" ? detailMsg : "Impossibile generare il PDF");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e.message || "Errore generazione PDF");
    }
  };

  const previewStrutturaPdf = async () => {
    if (!detail?.id || !associaTarget?.struttura_id) return;
    try {
      const res = await fetch(
        `${API}/api/admin/leads/${detail.id}/scheda-struttura-pdf?struttura_id=${encodeURIComponent(associaTarget.struttura_id)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.detail || "Impossibile generare la scheda struttura");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e.message || "Errore generazione PDF");
    }
  };

  const openStructureWhatsApp = (row, mode) => {
    setStructureWhatsAppTarget(row);
    setStructureWhatsAppMode(mode);
  };

  const confirmWhatsAppAvailability = async (availability) => {
    const fallback = availability.posti == null ? "" : String(availability.posti);
    const raw = window.prompt("Numero di posti disponibili da confermare", fallback);
    if (raw == null) return;
    const posti = Number(raw);
    if (!Number.isInteger(posti) || posti < 0) {
      toast.error("Inserisci un numero intero maggiore o uguale a zero.");
      return;
    }
    try {
      const res = await fetch(`${API}/api/admin/crm/whatsapp/disponibilita/${availability.id}/conferma`, {
        method: "POST",
        headers,
        body: JSON.stringify({ posti, da_quando: availability.da_quando || "" }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Conferma non riuscita");
      toast.success("Disponibilità confermata");
      runMatching();
      window.dispatchEvent(new Event("affidya-commerciale-menu-counts"));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const cols = useMemo(() => {
    const ids = [...visibleCols];
    if (isReteTab) {
      if (!ids.includes("struttura_affidya")) {
        const afterName = ids.indexOf("nome_lead");
        ids.splice(afterName >= 0 ? afterName + 1 : 1, 0, "struttura_affidya");
      }
      if (!ids.includes("gettone_stato")) ids.push("gettone_stato");
      if (!ids.includes("rete_stato")) ids.push("rete_stato");
    }
    return ids.map((id) => ALL_COLUMNS.find((c) => c.id === id)).filter(Boolean);
  }, [visibleCols, isReteTab]);

  const moveCol = (id, dir) => {
    const idx = visibleCols.indexOf(id);
    if (idx < 0) return;
    const j = idx + dir;
    if (j < 0 || j >= visibleCols.length) return;
    const next = [...visibleCols];
    [next[idx], next[j]] = [next[j], next[idx]];
    persistCols(next);
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const setWorkspace = (view) => {
    setWorkspaceView(view);
    const params = new URLSearchParams(window.location.search);
    params.set("cdrptView", view);
    window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
  };

  if (pipelineDetailId) {
    return <PraticaDetailPage id={pipelineDetailId} headers={headers} onBack={() => {
      setPipelineDetailId("");
      const params = new URLSearchParams(window.location.search);
      params.delete("pratica");
      window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
    }} />;
  }

  if (workspaceView === "pipeline") {
    return (
      <div className="space-y-4">
        <AdminPageHeader
          icon={Users}
          title="Richieste famiglie"
          description="Pipeline CDRPT: le stesse richieste dell'elenco, organizzate per fase"
          actions={<div className="flex rounded-md border border-admin-border p-0.5"><Button variant="ghost" size="sm" onClick={() => setWorkspace("elenco")}>Elenco</Button><Button variant="secondary" size="sm">Pipeline</Button></div>}
        />
        <PratichePipelineBoard headers={headers} onOpen={(id) => {
          setPipelineDetailId(id);
          const params = new URLSearchParams(window.location.search);
          params.set("pratica", id);
          window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
        }} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <AdminPageHeader
        icon={Users}
        title="Richieste famiglie"
        description="Lead CRM, richieste sito e pratiche Ospiti in Rete — click sulla riga per il dettaglio"
        actions={
        <>
          <div className="flex rounded-md border border-admin-border p-0.5">
            <Button variant="secondary" size="sm">Elenco</Button>
            <Button variant="ghost" size="sm" onClick={() => setWorkspace("pipeline")}>Pipeline</Button>
          </div>
          <Button variant="outline" size="sm" onClick={loadInbound}>
            <Settings2 className="h-4 w-4 mr-1" /> Email inbound
          </Button>
          <Button variant="outline" size="sm" onClick={() => setColsOpen(true)}>
            <Columns3 className="h-4 w-4 mr-1" /> Colonne
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1" /> Nuovo lead
          </Button>
        </>
        }
      />

      <Tabs value={viewTab} onValueChange={setViewTab}>
        <TabsList className="h-auto flex-wrap justify-start gap-1 bg-slate-100 p-1">
          {VIEW_TABS.map((tab) => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className={
                tab.id === "ospiti_rete"
                  ? "data-[state=active]:bg-amber-100 data-[state=active]:text-amber-900"
                  : undefined
              }
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs">Ricerca</Label>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              className="pl-8"
              placeholder={isReteTab ? "Nome, telefono, struttura…" : "Nome, telefono, comune…"}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
            />
          </div>
        </div>
        {!isReteTab && (
          <div className="w-40">
            <Label className="text-xs">Origine</Label>
            <Input
              placeholder="es. sito"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
            />
          </div>
        )}
        <Button variant="secondary" size="sm" onClick={load}>
          Filtra
        </Button>
      </div>

      {isReteTab && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Pratiche condivise dalle strutture: la data di inserimento è la data di condivisione;
          la colonna «Struttura che condivide» indica chi ha messo la richiesta in rete.
        </p>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto xl:max-h-[70vh]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b sticky top-0 z-10">
                <tr>
                  {cols.map((c) => (
                    <th
                      key={c.id}
                      className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase whitespace-nowrap cursor-pointer hover:bg-slate-100"
                      onClick={() => toggleSort(c.id)}
                    >
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        {sort === c.id && <ArrowUpDown className="h-3 w-3" />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={cols.length} className="px-4 py-10 text-center text-slate-500">
                      <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                      Caricamento…
                    </td>
                  </tr>
                ) : !items.length ? (
                  <tr>
                    <td colSpan={cols.length} className="px-4 py-10 text-center text-slate-500">
                      Nessun lead
                    </td>
                  </tr>
                ) : (
                  items.map((lead) => (
                    <tr
                      key={lead.id}
                      className="hover:bg-blue-50/40 cursor-pointer"
                      onClick={() => openDetail(lead)}
                    >
                      {cols.map((c) => (
                        <td key={c.id} className="px-3 py-2 whitespace-nowrap max-w-[220px] truncate">
                          {c.id === "nome_lead" && lead.is_nuovo ? (
                            <span className="inline-flex items-center gap-1.5">
                              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Nuovo</Badge>
                              {cellValue(lead, c.id)}
                            </span>
                          ) : c.id === "lead_status" ? (
                            <LeadStatusBadge status={lead.lead_status} />
                          ) : (
                            cellValue(lead, c.id)
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Colonne */}
      <Dialog open={colsOpen} onOpenChange={setColsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Colonne visibili e ordine</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            Spunta le colonne da mostrare e usa le frecce per cambiare l&apos;ordine nella griglia.
          </p>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {ALL_COLUMNS.map((c) => {
              const checked = visibleCols.includes(c.id);
              const orderIdx = visibleCols.indexOf(c.id);
              return (
                <div key={c.id} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(isChecked) => {
                      const next = isChecked
                        ? [...visibleCols, c.id]
                        : visibleCols.filter((x) => x !== c.id);
                      persistCols(next.length ? next : ["nome_lead"]);
                    }}
                  />
                  <span className="flex-1">{c.label}</span>
                  {checked && (
                    <span className="flex items-center gap-0.5">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        disabled={orderIdx <= 0}
                        onClick={() => moveCol(c.id, -1)}
                        title="Sposta su"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        disabled={orderIdx < 0 || orderIdx >= visibleCols.length - 1}
                        onClick={() => moveCol(c.id, 1)}
                        title="Sposta giù"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => persistCols(ALL_COLUMNS.filter((c) => c.default).map((c) => c.id))}
            >
              Ripristina default
            </Button>
            <Button onClick={() => setColsOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dettaglio */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="w-[96vw] max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {detail
                ? `${detail.first_name || ""} ${detail.last_name || ""}`.trim() ||
                  "Dettaglio lead"
                : "Lead"}
              {detail?.is_nuovo && (
                <Badge className="ml-2 bg-emerald-100 text-emerald-800">Nuovo</Badge>
              )}
              {detail?.parsing_status && detail.parsing_status !== "ok" && (
                <Badge
                  className="ml-2"
                  variant={detail.parsing_status === "incompleto" ? "destructive" : "secondary"}
                  title={(detail.parsing_diagnostics || []).join(", ")}
                >
                  Parsing {detail.parsing_status}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={openMatching}>
                  <MapPin className="h-4 w-4 mr-1" /> Trova strutture compatibili
                </Button>
                <Button size="sm" variant="outline" onClick={previewLeadPdf}>
                  <FileText className="h-4 w-4 mr-1" /> Anteprima scheda PDF
                </Button>
                <Button size="sm" variant="outline" onClick={() => setFamilyWhatsAppOpen(true)}>
                  <Phone className="h-4 w-4 mr-1" /> Invia WhatsApp
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditId(detail.id);
                    setForm({ ...EMPTY, ...detail });
                    setEditOpen(true);
                  }}
                >
                  Modifica
                </Button>
                <Button size="sm" variant="destructive" onClick={() => deleteLead(detail.id)}>
                  <Trash2 className="h-4 w-4 mr-1" /> Elimina
                </Button>
              </div>

              <div className="grid gap-2 rounded-xl border border-admin-border bg-admin-accent-soft/40 p-3 sm:grid-cols-3" aria-label="Riepilogo richiesta">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-admin-muted">Fase</p>
                  <LeadStatusBadge status={detail.lead_status} />
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-admin-muted">Ultimo contatto</p>
                  <p className="font-medium text-admin-text">{detail.data_ultimo_contatto ? new Date(detail.data_ultimo_contatto).toLocaleString("it-IT") : "Nessun contatto registrato"}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-admin-muted">Responsabile e prossima attività</p>
                  <p className="font-medium text-admin-text">{detail.pratica?.responsabile?.nome || "Non assegnata"}</p>
                  <p className="text-xs text-admin-muted">{detail.prossima_attivita?.testo || "Nessuna prossima attività"}</p>
                  <Button size="sm" variant="link" className="h-auto px-0" onClick={() => {
                    setDetail(null);
                    setPipelineDetailId(detail.id);
                    const params = new URLSearchParams(window.location.search);
                    params.set("pratica", detail.id);
                    window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
                  }}>Gestisci pratica</Button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                <div><span className="text-slate-500">Telefono referente:</span> {detail.phone || detail.mobile || "—"}</div>
                <div><span className="text-slate-500">Email referente:</span> {detail.email || "—"}</div>
                <div><span className="text-slate-500">Relazione con l'ospite:</span> {detail.relazione_referente || "—"}</div>
                <div><span className="text-slate-500">Comune di residenza:</span> {detail.city || "—"} {detail.state ? `(${detail.state})` : ""}</div>
                <div><span className="text-slate-500">Zone accettate:</span> {detail.zone_accettate || "—"}</div>
                <div><span className="text-slate-500">Zona richiesta (parsing):</span> {detail.zona_richiesta || "—"}</div>
                <div><span className="text-slate-500">Budget:</span> {detail.budget ?? "—"}</div>
                <div className="flex items-center gap-2"><span className="text-slate-500">Stato:</span> <LeadStatusBadge status={detail.lead_status} /></div>
                <div><span className="text-slate-500">Origine (portale):</span> {detail.portale_origine || detail.lead_source || "—"}</div>
                <div><span className="text-slate-500">Canale acquisizione:</span> {detail.canale_acquisizione || "—"}</div>
                <div>
                  <span className="text-slate-500">
                    {detail.ospiti_rete_richiesta_id || detail.origine === "richiesta_rete"
                      ? "Data condivisione:"
                      : "Data inserimento:"}
                  </span>{" "}
                  {(detail.data_condivisione_rete || detail.data_inserimento)
                    ? new Date(detail.data_condivisione_rete || detail.data_inserimento).toLocaleString("it-IT")
                    : "—"}
                </div>
                <div><span className="text-slate-500">Data ultimo contatto:</span> {detail.data_ultimo_contatto ? new Date(detail.data_ultimo_contatto).toLocaleString("it-IT") : "—"}</div>
                <div><span className="text-slate-500">Urgenza:</span> {detail.urgenza || "—"}</div>
                <div><span className="text-slate-500">Autosufficienza:</span> {detail.autosufficienza || "—"}</div>
                <div className="sm:col-span-2">
                  <span className="text-slate-500">{(detail.ospiti || []).length > 1 ? "Ospiti:" : "Ospite:"}</span>{" "}
                  {((detail.ospiti || []).length
                    ? detail.ospiti
                        .map((ospite) => [ospite.titolo, ospite.nome, ospite.cognome, ospite.eta, ospite.sesso].filter(Boolean).join(" · "))
                        .filter(Boolean)
                        .join(" / ")
                    : [detail.titolo_ospite, detail.nome_ospite, detail.cognome_ospite, detail.eta, detail.sesso].filter(Boolean).join(" · ")) || "—"}
                </div>
                {(detail.struttura_affidya || detail.rete_pratica?.struttura_nome) && (
                  <div className="col-span-2">
                    <span className="text-slate-500">Struttura che condivide:</span>{" "}
                    <span className="font-medium text-amber-900">
                      {detail.struttura_affidya || detail.rete_pratica?.struttura_nome}
                    </span>
                  </div>
                )}
              </div>
              {(detail.patologie?.length > 0 || detail.esigenze_assistenziali?.length > 0) && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-slate-500 mb-1">Patologie</p>
                    <p className="whitespace-pre-wrap">{(detail.patologie || []).join(", ") || "—"}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 mb-1">Esigenze assistenziali</p>
                    <p className="whitespace-pre-wrap">{(detail.esigenze_assistenziali || []).join(", ") || "—"}</p>
                  </div>
                </div>
              )}
              <div>
                <p className="text-slate-500 mb-1">Note contatto</p>
                <p className="whitespace-pre-wrap">{detail.note_contatto || "—"}</p>
              </div>
              {detail.description && detail.description !== detail.note_contatto ? (
                <div>
                  <p className="text-slate-500 mb-1">Dettaglio richiesta</p>
                  <p className="whitespace-pre-wrap">{detail.description}</p>
                </div>
              ) : null}
              {detail.parsing_status && detail.parsing_status !== "ok" && (detail.parsing_diagnostics || []).length > 0 && (
                <div className="border border-amber-300 bg-amber-50 rounded-lg p-2 text-amber-800">
                  <p className="font-medium">Diagnostica parsing email inbound</p>
                  <p className="text-xs">Campi minimi non compilati: {(detail.parsing_diagnostics || []).join(", ")}</p>
                </div>
              )}


              {!detail.pratica && <div className="border border-emerald-200 bg-emerald-50/40 rounded-lg p-3 space-y-3" data-testid="lead-economico-cdrpt">
                <h4 className="font-semibold flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-emerald-700" /> Economico CDRPT
                </h4>
                <p className="text-xs text-admin-muted">
                  Registrazione manuale di ingresso effettivo, fatture (350 € + IVA ciascuna) e pagamento.
                  Alimenta gli aggregati del tab Economico in Dashboard e report. Non genera fatture automatiche né note di credito.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    ["economico_ingresso_effettivo_at", "Ingresso effettivo"],
                    ["economico_fattura_1_inviata_at", "Fattura 1 inviata"],
                    ["economico_fattura_2_inviata_at", "Fattura 2 inviata"],
                    ["economico_pagamento_ricevuto_at", "Pagamento ricevuto"],
                  ].map(([field, label]) => (
                    <div key={field}>
                      <Label className="text-xs">{label}</Label>
                      <Input
                        type="date"
                        value={economicoForm[field] || ""}
                        onChange={(e) => setEconomicoForm((prev) => ({ ...prev, [field]: e.target.value }))}
                      />
                    </div>
                  ))}
                  <div>
                    <Label className="text-xs">Importo pagamento ricevuto (€, IVA inclusa)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={economicoForm.economico_pagamento_importo ?? ""}
                      onChange={(e) => setEconomicoForm((prev) => ({ ...prev, economico_pagamento_importo: e.target.value }))}
                      placeholder="es. 854.00"
                    />
                  </div>
                  <div className="flex items-end pb-1">
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={!!economicoForm.economico_annullato}
                        onCheckedChange={(v) => setEconomicoForm((prev) => ({ ...prev, economico_annullato: !!v }))}
                      />
                      Pratica annullata (esclusa dagli aggregati)
                    </label>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Note economiche (testo libero)</Label>
                  <Textarea
                    value={economicoForm.economico_note || ""}
                    onChange={(e) => setEconomicoForm((prev) => ({ ...prev, economico_note: e.target.value }))}
                    rows={2}
                    placeholder="Annotazioni, eccezioni, riferimenti fattura…"
                  />
                </div>
                <Button size="sm" onClick={saveEconomico} disabled={economicoSaving}>
                  {economicoSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                  Salva dati economici
                </Button>
              </div>}

              {canManageGettoni && detail.ospiti_rete_id && (
                <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-3 space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Coins className="h-4 w-4 text-amber-700" /> Gettone Ospiti in Rete
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      ["importo_totale_gettone", "Importo totale €"],
                      ["gettone_1_rata", "Gettone 1ª rata €"],
                      ["data_1_rata", "Data 1ª rata"],
                      ["saldo_1_rata", "Saldo 1ª rata €"],
                      ["gettone_2_rata", "Gettone 2ª rata €"],
                      ["data_2_rata", "Data 2ª rata"],
                      ["saldo_2_rata", "Saldo 2ª rata €"],
                      ["saldo_unico_pag", "Saldo unico €"],
                    ].map(([field, label]) => (
                      <div key={field}>
                        <Label className="text-xs">{label}</Label>
                        <Input
                          type={field.startsWith("data_") ? "date" : "number"}
                          value={gettoneForm[field] ?? ""}
                          onChange={(e) => setGettoneForm((prev) => ({ ...prev, [field]: e.target.value }))}
                        />
                      </div>
                    ))}
                    <div>
                      <Label className="text-xs">Stato saldo</Label>
                      <Select
                        value={gettoneForm.stato_saldo_gettone || "__empty__"}
                        onValueChange={(value) => setGettoneForm((prev) => ({
                          ...prev, stato_saldo_gettone: value === "__empty__" ? null : value,
                        }))}
                      >
                        <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__empty__">—</SelectItem>
                          {["IN ATTESA", "MATURATO", "SALDATO", "PAGATO"].map((value) => (
                            <SelectItem key={value} value={value}>{value}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Stato pagamento</Label>
                      <Select
                        value={gettoneForm.stato_pagamento || "__empty__"}
                        onValueChange={(value) => setGettoneForm((prev) => ({
                          ...prev, stato_pagamento: value === "__empty__" ? null : value,
                        }))}
                      >
                        <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__empty__">—</SelectItem>
                          {["In attesa", "Pagamento effettuato", "Parziale", "Annullato"].map((value) => (
                            <SelectItem key={value} value={value}>{value}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Note pagamento</Label>
                    <Textarea
                      value={gettoneForm.note_pagamento || ""}
                      onChange={(e) => setGettoneForm((prev) => ({ ...prev, note_pagamento: e.target.value }))}
                    />
                  </div>
                  <Button size="sm" onClick={saveGettone} disabled={gettoneSaving}>
                    {gettoneSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                    Salva gettone
                  </Button>
                </div>
              )}

              {canManagePratiche && detail.ospiti_rete_richiesta_id && (
                <div className="border border-blue-200 bg-blue-50/50 rounded-lg p-3 space-y-3">
                  <h4 className="font-semibold">Pratica Ospiti in Rete</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Stato pratica</Label>
                      <Select
                        value={praticaForm.stato || "Condiviso in rete"}
                        onValueChange={(value) => setPraticaForm((prev) => ({ ...prev, stato: value }))}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["Nuova", "Da seguire", "Documenti richiesti", "Condiviso in rete", "Inserito", "Chiuso"].map((value) => (
                            <SelectItem key={value} value={value}>{value}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Stato gettone</Label>
                      <Select
                        value={praticaForm.stato_gettone || "In attesa"}
                        onValueChange={(value) => setPraticaForm((prev) => ({ ...prev, stato_gettone: value }))}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["In attesa", "Maturato", "Saldato"].map((value) => (
                            <SelectItem key={value} value={value}>{value}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Gettone previsto €</Label>
                      <Input
                        type="number"
                        value={praticaForm.gettone_previsto ?? ""}
                        onChange={(e) => setPraticaForm((prev) => ({ ...prev, gettone_previsto: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Data maturazione</Label>
                      <Input
                        type="date"
                        value={praticaForm.data_maturazione || ""}
                        onChange={(e) => setPraticaForm((prev) => ({ ...prev, data_maturazione: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Data saldo</Label>
                      <Input
                        type="date"
                        value={praticaForm.data_saldo || ""}
                        onChange={(e) => setPraticaForm((prev) => ({ ...prev, data_saldo: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Note rete</Label>
                    <Textarea
                      value={praticaForm.note_rete || ""}
                      onChange={(e) => setPraticaForm((prev) => ({ ...prev, note_rete: e.target.value }))}
                    />
                  </div>
                  <Button size="sm" onClick={saveRetePratica} disabled={praticaSaving}>
                    {praticaSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                    Salva pratica
                  </Button>
                </div>
              )}

              <div>
                <h4 className="font-semibold mb-2">Strutture proposte ({(detail.strutture_proposte || []).length})</h4>
                {!(detail.strutture_proposte || []).length ? (
                  <p className="text-slate-500">Nessuna proposta — usa il matching</p>
                ) : (
                  <ul className="space-y-2">
                    {(detail.strutture_proposte || []).map((p) => (
                      <li key={p.id} className="border rounded-lg p-2 bg-slate-50">
                        <div className="font-medium">{p.struttura_nome || p.struttura_id}</div>
                        <div className="text-xs text-slate-500">
                          {p.data_proposta ? new Date(p.data_proposta).toLocaleString("it-IT") : ""} ·{" "}
                          {p.stato_interazione || "—"}
                        </div>
                        {p.note && <p className="text-xs mt-1">{p.note}</p>}
                        {p.esito && <p className="text-xs text-slate-600">Esito: {p.esito}</p>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <h4 className="font-semibold mb-2">Storico</h4>
                <div className="flex gap-2 mb-2">
                  <Input placeholder="Nuova nota…" value={nota} onChange={(e) => setNota(e.target.value)} />
                  <Button size="sm" onClick={addNota}>Aggiungi</Button>
                </div>
                <ul className="space-y-1 max-h-56 overflow-y-auto">
                  {(detail.storico || []).slice().reverse().map((s) => (
                    <li key={s.id} className="text-xs border-b py-1">
                      <span className="text-slate-400">
                        {s.at ? new Date(s.at).toLocaleString("it-IT") : ""} · {s.tipo}
                        {s.mittente ? ` · ${s.mittente}` : ""}
                      </span>
                      <p className="whitespace-pre-wrap line-clamp-4">{s.testo}</p>
                      {s.allegati?.length > 0 && (
                        <p className="text-slate-500 mt-0.5">
                          Allegati: {s.allegati.map((a) => `${a.filename} (${a.metodo_estrazione})`).join(", ")}
                        </p>
                      )}
                      {s.errori_parsing?.length > 0 && (
                        <p className="text-amber-700 mt-0.5">⚠ {s.errori_parsing.join(" · ")}</p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Matching */}
      <Dialog open={matchOpen} onOpenChange={setMatchOpen}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Strutture compatibili</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mb-3">
            <div className="relative">
              <Label className="text-xs">Indirizzo o zona di partenza</Label>
              <Input
                value={origine}
                onChange={(e) => {
                  setOrigine(e.target.value);
                  setOriginSelected(null);
                  setOriginSuggestOpen(true);
                }}
                onFocus={() => originSuggestions.length > 0 && setOriginSuggestOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (!originSelected && originSuggestions[0]) {
                      pickOrigin(originSuggestions[0]);
                    }
                    runMatching({
                      originSelected: originSelected || originSuggestions[0] || null,
                    });
                  }
                }}
                placeholder="Via o zona"
                autoComplete="off"
              />
              {originSuggestLoading && (
                <Loader2 className="absolute right-2 top-8 h-4 w-4 animate-spin text-slate-400" />
              )}
              {originSuggestOpen && originSuggestions.length > 0 && (
                <ul className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg text-sm">
                  {originSuggestions.map((s) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        className={`w-full text-left px-3 py-2 hover:bg-blue-50 ${
                          originSelected?.id === s.id ? "bg-blue-50" : ""
                        }`}
                        onClick={() => pickOrigin(s)}
                      >
                        <div className="font-medium text-slate-800">{s.label}</div>
                        <div className="text-[11px] text-slate-500">
                          {s.source === "google"
                            ? "Google Maps"
                            : s.source === "nominatim"
                              ? "OpenStreetMap"
                              : s.source === "locale"
                                ? "Zona nota"
                                : s.source === "cache"
                                  ? "Già geocodificato"
                                  : s.source}
                          {s.formatted_address && s.formatted_address !== s.label
                            ? ` · ${s.formatted_address}`
                            : ""}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-[11px] text-slate-500 mt-1">
                Scrivi e scegli una corrispondenza dall&apos;elenco. Il raggio è un calcolo locale gratuito;
                Google Geocoding è a pagamento solo se non c&apos;è già in cache (vedi Admin → Google Maps API).
              </p>
            </div>
            {(originSelected || (matchMeta?.origine?.lat != null && matchMeta?.origine?.lng != null)) && (
              <div
                className={`rounded-lg border p-3 ${
                  (originSelected || matchMeta?.origine?.source === "selezione" || matchMeta?.origine?.source === "google" || matchMeta?.origine?.source === "nominatim" || matchMeta?.origine?.source === "cache" || matchMeta?.origine?.source === "locale")
                    ? "border-emerald-200 bg-emerald-50/70"
                    : matchMeta?.origine?.source === "comune"
                      ? "border-amber-200 bg-amber-50"
                      : "border-slate-200 bg-slate-50"
                }`}
              >
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900">
                      Punto di partenza riconosciuto
                    </p>
                    <p className="text-sm text-slate-700">
                      {originSelected?.formatted_address ||
                        originSelected?.label ||
                        matchMeta?.origine?.formatted_address ||
                        "—"}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {(originSelected?.lat ?? matchMeta?.origine?.lat)?.toFixed?.(5)} ,{" "}
                      {(originSelected?.lng ?? matchMeta?.origine?.lng)?.toFixed?.(5)}
                      {" · "}
                      {originSelected?.source === "google" || matchMeta?.origine?.source === "google"
                        ? "Google Maps"
                        : originSelected?.source === "nominatim" || matchMeta?.origine?.source === "nominatim"
                          ? "OpenStreetMap"
                          : originSelected?.source === "mappa" || matchMeta?.origine?.source === "mappa"
                            ? "Punto sulla mappa"
                          : originSelected?.source === "locale" || matchMeta?.origine?.source === "locale"
                            ? "Zona nota"
                            : matchMeta?.origine?.source === "comune"
                              ? "Stima sul comune"
                              : "Coordinate"}
                    </p>
                  </div>
                </div>
                <OriginPickerMap
                  lat={Number(originSelected?.lat ?? matchMeta?.origine?.lat)}
                  lng={Number(originSelected?.lng ?? matchMeta?.origine?.lng)}
                  onDragEnd={handleOriginMapMove}
                />
              </div>
            )}
            {matchMeta?.origine?.source === "none" && !originSelected && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 flex gap-2 text-sm text-red-800">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  Punto di partenza non riconosciuto. Scegli un risultato dall&apos;elenco sopra, poi premi Ricalcola.
                </span>
              </div>
            )}
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <Label className="text-xs">Budget max €</Label>
                <Input
                  type="number"
                  min="0"
                  value={budgetMax}
                  onChange={(e) => setBudgetMax(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      runMatching();
                    }
                  }}
                  className="w-32"
                  placeholder="es. 1800"
                />
              </div>
              <div>
                <Label className="text-xs">Raggio km</Label>
                <Input
                  type="number"
                  min="1"
                  value={raggio}
                  onChange={(e) => setRaggio(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      runMatching();
                    }
                  }}
                  className="w-24"
                />
              </div>
              <label className="flex items-center gap-2 text-xs pb-2">
                <Checkbox
                  checked={includiOltreBudget}
                  onCheckedChange={(v) => setIncludiOltreBudget(!!v)}
                />
                Includi rette oltre budget
              </label>
              <Button size="sm" onClick={() => runMatching()} disabled={matchLoading}>
                {matchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ricalcola"}
              </Button>
            </div>
            {matchMeta?.origine && (
              <p className="text-xs text-slate-600">
                Partenza:{" "}
                <span className="font-medium">
                  {matchMeta.origine.formatted_address || matchMeta.origine.query || "—"}
                </span>
                {matchMeta.origine.source === "google" || matchMeta.origine.source === "cache"
                  ? " (coordinate Google Maps)"
                  : matchMeta.origine.source === "nominatim"
                    ? " (OpenStreetMap)"
                    : matchMeta.origine.source === "locale" || matchMeta.origine.source === "selezione" || matchMeta.origine.source === "mappa"
                      ? " (punto scelto)"
                  : matchMeta.origine.source === "comune"
                    ? " (stima sul comune)"
                    : ""}
                {matchMeta.budget != null ? ` · budget € ${matchMeta.budget}` : ""}
                {matchMeta.raggio_km != null ? ` · raggio ${matchMeta.raggio_km} km` : ""}
                {matchMeta.compatibili != null ? ` · ${matchMeta.compatibili} compatibili` : ""}
              </p>
            )}
            {(matchMeta?.avvisi || []).map((avviso) => (
              <p key={avviso} className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                {avviso}
              </p>
            ))}
            {matchMeta?.geocoding && !matchMeta.geocoding.disabled && (
              <p className="text-[11px] text-slate-500">
                Coordinate strutture: {matchMeta.geocoding.geocoded || 0} nuove
                {matchMeta.geocoding.failed ? `, ${matchMeta.geocoding.failed} non trovate` : ""}
                {matchMeta.geocoding.skipped_cap || matchMeta.geocoding.skipped_timeout
                  ? `, ${(matchMeta.geocoding.skipped_cap || 0) + (matchMeta.geocoding.skipped_timeout || 0)} rimandate`
                  : ""}
                . Solo le nuove geocodifiche Google hanno costo (Admin → Google Maps API → Matching richieste famiglie).
              </p>
            )}
          </div>
          {matchLoading ? (
            <p className="text-center py-8 text-slate-500">Calcolo matching…</p>
          ) : matchResults.length === 0 ? (
            <p className="text-center py-8 text-slate-500">
              Nessuna struttura nel raggio e budget indicati
            </p>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-2 py-1 text-left">Struttura</th>
                  <th className="px-2 py-1 text-left">Distanza</th>
                  <th className="px-2 py-1 text-left">Retta</th>
                  <th className="px-2 py-1 text-left">Posti</th>
                  <th className="px-2 py-1 text-left">Compatibilità</th>
                  <th className="px-2 py-1 text-left">Motivazione</th>
                  <th className="px-2 py-1" />
                </tr>
              </thead>
              <tbody>
                {matchResults.map((r) => (
                  <tr key={r.struttura_id} className={r.esclusa ? "opacity-50" : ""}>
                    <td className="px-2 py-1.5">
                      <div className="font-medium">{r.nome}</div>
                      <div className="text-[11px] text-slate-500">
                        {r.indirizzo || r.comune || ""}
                      </div>
                    </td>
                    <td className="px-2 py-1.5">
                      {r.distanza_km != null ? `${r.distanza_km} km` : "—"}
                      {r.coords_source === "comune" && r.distanza_km != null ? (
                        <span className="block text-[10px] text-slate-400">stima comune</span>
                      ) : null}
                    </td>
                    <td className="px-2 py-1.5">{r.retta != null ? `€ ${r.retta}` : "—"}</td>
                    <td className="px-2 py-1.5">
                      {r.disponibilita ?? "—"}
                      <WhatsAppDisponibilitaBadge
                        availability={r.whatsapp?.disponibilita}
                        onConfirm={confirmWhatsAppAvailability}
                        onConversation={(conversationKey) => {
                          window.location.href = `/admin?module=commerciale-whatsapp&conversation=${encodeURIComponent(conversationKey)}`;
                        }}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <Badge variant={r.compatibilita >= 60 ? "default" : "secondary"}>
                        {r.compatibilita}%
                      </Badge>
                    </td>
                    <td className="px-2 py-1.5 text-xs max-w-xs">
                      {(r.motivazioni || []).join("; ")}
                      {(r.esclusioni || []).length > 0 && (
                        <span className="text-red-600"> · {(r.esclusioni || []).join("; ")}</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-3">
                        {!r.esclusa && (
                          <div className="flex shrink-0 gap-1">
                            <Button size="sm" variant="outline" onClick={() => openAssocia(r)}>
                              <Link2 className="h-3 w-3 mr-1" /> Associa
                            </Button>
                            <Button size="sm" onClick={() => openAssocia(r, { inviaSchede: true })}>
                              <FileText className="h-3 w-3 mr-1" /> Invia schede
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openStructureWhatsApp(r, "scheda")}>
                              <Phone className="h-3 w-3 mr-1" /> Scheda lead WA
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openStructureWhatsApp(r, "posti")}>
                              <Phone className="h-3 w-3 mr-1" /> Chiedi posti
                            </Button>
                          </div>
                        )}
                        <ContattiStruttura contatti={r.contatti} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <WhatsAppFamigliaDialog
        open={familyWhatsAppOpen}
        onOpenChange={setFamilyWhatsAppOpen}
        lead={detail}
        headers={headers}
      />
      {structureWhatsAppTarget && structureWhatsAppMode === "scheda" && (
        <WhatsAppSchedaLeadDialog
          open
          onOpenChange={(open) => !open && setStructureWhatsAppTarget(null)}
          lead={detail}
          structure={structureWhatsAppTarget}
          headers={headers}
          onSent={() => runMatching()}
        />
      )}
      {structureWhatsAppTarget && structureWhatsAppMode === "posti" && (
        <WhatsAppRichiestaPostiDialog
          open
          onOpenChange={(open) => !open && setStructureWhatsAppTarget(null)}
          lead={detail}
          structure={structureWhatsAppTarget}
          headers={headers}
          onSent={() => runMatching()}
        />
      )}

      {/* Associa + invio schede */}
      <Dialog open={associaOpen} onOpenChange={setAssociaOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {sendBothSchede
                ? `Associa e invia le schede per ${associaTarget?.nome || ""}`
                : `Associa ${associaTarget?.nome || ""}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {sendBothSchede ? (
              <p className="text-sm text-slate-600 rounded-md bg-blue-50 border border-blue-100 p-3">
                Verrà registrata la proposta e saranno inviate la scheda della struttura alla famiglia
                e la scheda orientamento alla struttura.
              </p>
            ) : (
              <div>
                <Label>Azione</Label>
                <Select
                  value={associaForm.azione_invio}
                  onValueChange={(v) => setAssociaForm((p) => ({ ...p, azione_invio: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="solo_registra">Registra soltanto la proposta</SelectItem>
                    <SelectItem value="scheda_struttura">Invia scheda struttura al lead</SelectItem>
                    <SelectItem value="scheda_lead">Invia scheda lead alla struttura</SelectItem>
                    <SelectItem value="entrambe">Invia entrambe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {associaForm.azione_invio !== "solo_registra" && (
              <>
                <div>
                  <Label>Testo email (modificabile)</Label>
                  <Textarea
                    rows={5}
                    value={associaForm.email_testo}
                    onChange={(e) => setAssociaForm((p) => ({ ...p, email_testo: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Destinatario famiglia / lead</Label>
                  <Input
                    value={associaForm.destinatario_lead}
                    onChange={(e) => setAssociaForm((p) => ({ ...p, destinatario_lead: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Destinatario struttura</Label>
                  <Input
                    value={associaForm.destinatario_struttura}
                    onChange={(e) =>
                      setAssociaForm((p) => ({ ...p, destinatario_struttura: e.target.value }))
                    }
                  />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={associaForm.nascondi_nome}
                    onCheckedChange={(v) => setAssociaForm((p) => ({ ...p, nascondi_nome: !!v }))}
                  />
                  Nascondi nome completo nella scheda per la struttura
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={associaForm.nascondi_email}
                    onCheckedChange={(v) => setAssociaForm((p) => ({ ...p, nascondi_email: !!v }))}
                  />
                  Nascondi email nella scheda per la struttura
                </label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={previewLeadPdf}
                >
                  Anteprima scheda lead PDF
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={previewStrutturaPdf}
                >
                  Anteprima scheda struttura PDF
                </Button>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssociaOpen(false)}>Annulla</Button>
            <Button onClick={confirmAssocia} disabled={associating}>
              {associating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Conferma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? "Modifica lead" : "Nuovo lead"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            {[
              ["first_name", "Nome"],
              ["last_name", "Cognome"],
              ["phone", "Telefono"],
              ["email", "Email"],
              ["city", "Comune di residenza"],
              ["budget", "Budget"],
              ["lead_source", "Origine"],
              ["nome_ospite", "Nome ospite"],
              ["cognome_ospite", "Cognome ospite"],
              ["eta", "Età"],
              ["sesso", "Sesso"],
              ["relazione_referente", "Relazione referente"],
              ["portale_origine", "Portale origine"],
              ["zona_richiesta", "Zona (da email/PDF)"],
              ["autosufficienza", "Autosufficienza"],
              ["urgenza", "Urgenza"],
            ].map(([k, lab]) => (
              <div key={k}>
                <Label className="text-xs">{lab}</Label>
                <Input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} />
              </div>
            ))}
            <div>
              <Label className="text-xs">Stato</Label>
              <Select
                value={form.lead_status || "Nuovo"}
                onValueChange={(v) => set("lead_status", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                  {form.lead_status && !LEAD_STATUS_OPTIONS.includes(form.lead_status) && (
                    <SelectItem value={form.lead_status}>{form.lead_status}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Zone accettate</Label>
              <Input
                value={form.zone_accettate || ""}
                onChange={(e) => set("zone_accettate", e.target.value)}
                placeholder="Es. Roma nord, Tivoli, Castelli romani…"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Note</Label>
              <Textarea value={form.note_contatto || ""} onChange={(e) => set("note_contatto", e.target.value)} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button>
            <Button onClick={saveLead} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inbound config */}
      <Dialog open={inboundOpen} onOpenChange={setInboundOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Email acquisizione lead</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            Indirizzo Resend inbound (stessa logica del deblistering). Webhook:{" "}
            <code className="text-xs">/api/webhooks/leads/inbound-email</code>
          </p>
          <div className="space-y-2">
            <Label>Email inbox</Label>
            <Input
              value={inboundCfg.email_inbox || ""}
              onChange={(e) => setInboundCfg((p) => ({ ...p, email_inbox: e.target.value }))}
              placeholder="lead@email.affidya.it"
            />
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={!!inboundCfg.enabled}
                onCheckedChange={(v) => setInboundCfg((p) => ({ ...p, enabled: !!v }))}
              />
              Abilitato
            </label>
          </div>
          <div className="border-t pt-3 space-y-2">
            <h4 className="font-semibold text-sm">Operatori CRM</h4>
            <p className="text-xs text-slate-500">
              Profilo Operatore CRM (Lead, Strutture, Immobili). Per altri profili (es. Editor
              contenuti) usa Sistema → Utenti admin.
            </p>
            <ul className="text-xs space-y-1 max-h-28 overflow-y-auto">
              {crmUsers.map((u) => (
                <li key={u.id} className="flex justify-between gap-2">
                  <span>{u.email} {u.name ? `(${u.name})` : ""}</span>
                  <Badge variant={u.active ? "default" : "secondary"}>
                    {u.active ? "attivo" : "off"}
                  </Badge>
                </li>
              ))}
            </ul>
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="Email" value={crmForm.email} onChange={(e) => setCrmForm((p) => ({ ...p, email: e.target.value }))} />
              <Input placeholder="Nome" value={crmForm.name} onChange={(e) => setCrmForm((p) => ({ ...p, name: e.target.value }))} />
              <Input type="password" placeholder="Password" value={crmForm.password} onChange={(e) => setCrmForm((p) => ({ ...p, password: e.target.value }))} />
            </div>
            <Button size="sm" variant="secondary" onClick={createCrmUser}>Crea operatore</Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInboundOpen(false)}>Annulla</Button>
            <Button onClick={saveInbound}>Salva email</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
